import type { ViewStyle } from 'react-native';
import { useWindowDimensions } from 'react-native';
import {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import {
  LOCATION_RESULTS_COLLAPSED_HEIGHT_MULTIPLE_RESULTS,
  LOCATION_RESULTS_COLLAPSED_HEIGHT_SINGLE_OTHER_LOCATION_RESULT,
  LOCATION_RESULTS_COLLAPSED_HEIGHT_SINGLE_RESULT,
} from '../../LocationResultsV2.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const useLocationResultsExpandCollapseStyles = (
  props: UseLocationResultsExpandCollapseStylesProps,
) => {
  const { isExpanded, hasMultipleResults, hasSingleOtherResult } = props;

  // NOTE: We utilize viewport height as a default value to completely hide the results
  //       container on initial render until it is managed by the associated component.
  const { height: viewportHeight } = useWindowDimensions();
  const outerContainerHeight = useSharedValue(viewportHeight);

  const innerContainerCollapsedHeight = getLocationResultsCollapsedHeight({
    hasMultipleResults,
    hasSingleOtherResult,
  });

  const innerContainerTranslateY = useDerivedValue(() => {
    const expandedViewTranslateY = 0;
    const collapsedViewTranslateY =
      outerContainerHeight.value - innerContainerCollapsedHeight;

    return withTiming(
      isExpanded ? expandedViewTranslateY : collapsedViewTranslateY,
    );
  });

  const innerContainerAnimatedStyle = useAnimatedStyle(() => {
    return { transform: [{ translateY: innerContainerTranslateY.value }] };
  });
  const innerContainerCollapsedStyle: ViewStyle = {
    height: innerContainerCollapsedHeight,
  };

  // ─────────────────────────────────────────────────────────────────────

  return {
    outerContainerHeight,
    innerContainerCollapsedStyle,
    innerContainerAnimatedStyle,
  };
};

// ─── Utils ───────────────────────────────────────────────────────────────────

/**
 * Returns location results collapsed container height depending on multiple conditions:
 *
 * - Whether a single location result is displayed
 * - Whether a single other location result is displayed
 */
export function getLocationResultsCollapsedHeight(
  props: Pick<
    UseLocationResultsExpandCollapseStylesProps,
    'hasMultipleResults' | 'hasSingleOtherResult'
  >,
) {
  const { hasMultipleResults, hasSingleOtherResult } = props;

  if (hasSingleOtherResult)
    return LOCATION_RESULTS_COLLAPSED_HEIGHT_SINGLE_OTHER_LOCATION_RESULT;

  if (hasMultipleResults)
    return LOCATION_RESULTS_COLLAPSED_HEIGHT_MULTIPLE_RESULTS;

  return LOCATION_RESULTS_COLLAPSED_HEIGHT_SINGLE_RESULT;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseLocationResultsExpandCollapseStylesProps = {
  readonly hasMultipleResults: boolean;
  readonly hasSingleOtherResult: boolean;
  readonly isExpanded: boolean;
};
